// The box at the top of the shell (ZENITH.md §18; ADR-0324 decisions 2 and 3,
// its six menus ruled 2026-10-14 on #1604, menus 4 and 6 re-ruled 2026-10-17
// on #1843). One field beside the scope switcher that asks `GET /v1/search`
// and shows what it answers, every class (menu 1a), when the person's
// typing pauses for SEARCH_PAUSE_MS or they press Enter (6b). The dropdown
// shows results and nothing else (4a): what the search does not reach is said
// in the answer's own `not_searched`, not in the box's copy.
//
// **A MAC is one kind of query.** A whole address in any spelling finds the
// device whose serial it is: the server reduces it (ADR-0324 decision 1), so
// nothing here rewrites what is sent. Where it matched no device, the box
// hands the address to one site's Clients page by its `mac` facet (decision
// 2): the site in scope, or, with none, the scope picker narrowed to sites,
// landing on the site chosen with the address kept (2b). A part of an address
// gets no link (5a), because the facet takes a whole one.
//
// **A whole ID is another** (ADR-0551, #2347). A customer's, a site's, a
// network's or an organization's UUID, pasted whole (as #2344's menus copy it),
// finds that object: the server matches it, so nothing here reads the term.
// An organization comes back as an `org` hit, a link that makes it the scope,
// with its menu.
//
// **A miss and an out-of-scope hit are one answer** (ADR-0324, *How a miss
// and an out-of-scope hit stay the same answer*; ADR-0109 decision 4). The
// server scopes before it matches and answers the same empty set for both,
// and everything below is derived from that answer and the text typed. It
// names no row it was not served, and nothing here shows a count.
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { describe as describeProblem, type Problem } from "../api/problem";
import type { components } from "../api/schema";
import { Glyph, SearchIcon } from "../icons";
import { When } from "../ui";
import { useEstate } from "./estate";
import { facetSearch, type Facets } from "./facets";
import { ScopeMenu, type Row } from "./ScopePicker";
import { useNarrow } from "./narrow";
import { EntityMenuButton } from "./EntityRef";
import type { Entity } from "./entity";
import { scopeSearch, useScope, type Scope } from "./scope";

type SearchResults = components["schemas"]["SearchResults"];
type Hit = SearchResults["items"][number];

type Answer =
  | { q: string; state: "asking" }
  | { q: string; state: "failed"; problem: Problem }
  | { q: string; state: "answered"; data: SearchResults };

/** The twelve hex digits a whole MAC address spells, in the `mac` facet's
 * spelling, or undefined for anything else. The server's rule for `q`
 * (ADR-0324 decision 1, its second menu ruled (a)): `:`, `-`, `.` and white
 * space are separators, any other character means it is not an address. It is
 * read here only to decide the hand-off and to fill the facet; what the search
 * matched is the server's. */
export function wholeAddress(text: string): string | undefined {
  let hex = "";
  for (const ch of text.trim()) {
    if (/[:\-.\s]/.test(ch)) continue;
    if (!/[0-9a-fA-F]/.test(ch)) return undefined;
    hex += ch.toLowerCase();
  }
  return hex.length === 12 ? hex : undefined;
}

const formatted = (mac: string) => mac.match(/../g)!.join(":");

/** How long typing pauses before the box asks (ADR-0324 decision 2, menu 6
 * re-ruled (b) on 2026-10-17, #1843). A choice made for the interface, not a
 * measured or ruled value: it bounds how often one person's typing asks, sets
 * no limit on the server, and nothing downstream depends on it. Raising it
 * asks less often. */
export const SEARCH_PAUSE_MS = 300;

/** The longest `q` the API takes (`searchTermMax` in handlers_search.go). A
 * pause on a longer text asks nothing; Enter still asks, so the API's own
 * refusal is what the person sees. The least length is the API's too: one
 * character, so a blank text asks nothing and anything else may. */
const SEARCH_TERM_MAX = 120;

export default function SearchBox() {
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState<Answer>();
  const [open, setOpen] = useState(false);
  const pause = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inFlight = useRef<{ q: string; stop: AbortController }>(undefined);
  const close = () => setOpen(false);

  const stopAsking = () => { inFlight.current?.stop.abort(); inFlight.current = undefined; };
  useEffect(() => () => { clearTimeout(pause.current); stopAsking(); }, []);

  const ask = (q: string) => {
    clearTimeout(pause.current);
    stopAsking();
    const stop = new AbortController();
    inFlight.current = { q, stop };
    setOpen(true);
    setAnswer({ q, state: "asking" });
    // An answer for a text that is no longer the one asked is dropped, whether
    // or not the abort reached it in time.
    const settle = (next: Answer) => {
      if (inFlight.current?.stop === stop) inFlight.current = undefined;
      setAnswer((a) => (a?.q === q ? next : a));
    };
    api.GET("/search", { params: { query: { q } }, signal: stop.signal }).then(({ data, error, response }) => {
      if (stop.signal.aborted) return;
      if (!data || error) { settle({ q, state: "failed", problem: describeProblem(error, response.status) }); return; }
      settle({ q, state: "answered", data });
    }, () => { if (!stop.signal.aborted) settle({ q, state: "failed", problem: describeProblem(undefined) }); });
  };

  const change = (value: string) => {
    setText(value);
    clearTimeout(pause.current);
    const q = value.trim();
    if (q === "") { stopAsking(); setAnswer(undefined); close(); return; }
    // Only white space changed: the search already asked is still the box's.
    if (inFlight.current?.q === q) return;
    stopAsking();
    if ([...q].length > SEARCH_TERM_MAX) return;
    pause.current = setTimeout(() => ask(q), SEARCH_PAUSE_MS);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = text.trim();
    if (q !== "") ask(q);
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };

  // On a phone the field is one icon beside the scope until it is wanted
  // (UX-018): the scope, the field and the changes stacked to a third of the
  // screen before the page's title. Tapped, it opens across the row with the
  // keyboard up; emptied and left, it folds back. The field is the same field.
  const narrow = useNarrow();
  const [unfolded, setUnfolded] = useState(false);
  const folded = narrow && !unfolded && text === "";
  // The tap that unfolds it asked for the field: it takes focus once drawn.
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => { if (narrow && unfolded) field.current?.focus(); }, [narrow, unfolded]);
  if (folded) {
    return (
      <form className="shell-search seg" role="search" aria-label="Search" onSubmit={(e) => e.preventDefault()}>
        <button type="button" className="btn btn-quiet" aria-label="Search" onClick={() => setUnfolded(true)}>
          <Glyph icon={SearchIcon} />
        </button>
      </form>
    );
  }

  return (
    <form className={"shell-search seg" + (narrow ? " unfolded" : "")} role="search" aria-label="Search" onSubmit={submit}>
      <input type="search" className="shell-search-input" aria-label="Search" placeholder="Search" ref={field}
        value={text} onChange={(e) => change(e.target.value)} onKeyDown={onKey}
        onBlur={() => { if (narrow && text.trim() === "" && !open) setUnfolded(false); }} />
      {open && answer && (
        <>
          {/* A click-away layer, not a control: Escape closes the panel for the keyboard. */}
          <div className="backdrop" role="presentation" onClick={close} />
          <div className="rail-menu shell-search-panel" role="presentation" onKeyDown={onKey}>
            <section aria-label="Search results"><Results answer={answer} onDone={close} /></section>
          </div>
        </>
      )}
    </form>
  );
}

function Results({ answer, onDone }: { answer: Answer; onDone(): void }) {
  if (answer.state === "asking") return <p className="muted" role="status">Searching…</p>;
  if (answer.state === "failed") return <p className="muted" role="status">The search did not answer. {answer.problem.message}</p>;
  const { data, q } = answer;
  const mac = wholeAddress(q);
  // The hand-off is offered on the address having matched no device the
  // caller can see, and on nothing else (ADR-0324). A radio address of an
  // access point is not a match of the device: it is shown beside it.
  const handOff = mac !== undefined && !data.items.some((h) => h.kind === "claimed_device" && h.serial === mac);
  // Only the short lines are a live region, so an answer arriving after a
  // pause does not read the whole list aloud while the person types.
  return (
    <div>
      {data.items.length > 0
        ? <ul className="shell-search-hits">{data.items.map((h, i) => (
          // A line chosen in a hit's menu closes the panel as following the hit does; the
          // menu is drawn in a portal, and React carries its click to this item (#2344).
          <li key={i}><span role="presentation" onClick={(e) => { if ((e.target as HTMLElement).closest("[role=menuitem]")) onDone(); }}>
            <HitLink hit={h} onDone={onDone} />{entityOfHit(h) && <EntityMenuButton entity={entityOfHit(h)!} />}
          </span></li>))}</ul>
        // What this search answered, and nothing about what exists: not "nothing you can see", since a device its
        // site's Devices page lists may be found only in Zenith's copy of its cell's inventory, which is as old as
        // the sweep's last tick (ADR-0397, #1805). The same line for a miss and an out-of-scope hit (ADR-0324).
        : <p className="muted" role="status">No results</p>}
      {/* A cut list is not a complete one (ADR-0109, *What becomes harder*). */}
      {data.more.length > 0 && <p className="small muted">More match. Type more to narrow them.</p>}
      {handOff && <HandOff mac={mac} onDone={onDone} />}
    </div>
  );
}

/** Where a hit is, as a scope. */
const scopeOf = (h: Hit, level: "customer" | "site"): Scope => ({
  orgId: h.org_id, customerId: h.customer_id, siteId: level === "site" ? h.site_id : undefined,
});

/** A link to `pathname` at `scope` with `facets`, whatever scope this page is at. */
function At({ scope, pathname, facets = {}, onDone, children }: { scope: Scope; pathname: string; facets?: Facets; onDone(): void; children: ReactNode }) {
  return <Link to={{ pathname, search: facetSearch(facets, scopeSearch(scope)) }} onClick={onDone}>{children}</Link>;
}

/** One hit, as the way to it, with where it is. A name the server did not
 * serve is not shown: an access point's is the controller's, and a parent's
 * is served only where the caller's role reaches it (§6.3). */
function HitLink({ hit: h, onDone }: { hit: Hit; onDone(): void }) {
  const where = [h.site_name, h.customer_name].filter(Boolean).join(", ");
  const place = where ? <span className="muted"> · {where}</span> : null;
  switch (h.kind) {
    case "claimed_device":
      return <><At scope={scopeOf(h, "site")} pathname="/operate/devices" facets={{ serial: h.serial }} onDone={onDone}>
        {h.device_class === "switch" ? "Switch" : "Access point"} {h.name ?? h.serial}</At>{place}<Listed at={h.inventory_as_of} /></>;
    case "radio_address":
      // Restates ADR-0324 decision 3 — the address is a radio address of this access point, never its own
      // address and never the access point as the address's identity; shown beside the hand-off.
      return <><At scope={scopeOf(h, "site")} pathname="/operate/devices" facets={{ serial: h.serial }} onDone={onDone}>
        A radio address of access point {h.serial}</At>{place}<Listed at={h.inventory_as_of} /><Reported at={h.radio_recorded_at} /></>;
    case "planned_device":
      return <><At scope={scopeOf(h, "site")} pathname="/design/physical" onDone={onDone}>Planned device {h.name}</At>{place}</>;
    case "customer":
      return <><At scope={{ orgId: h.org_id, customerId: h.id }} pathname="/" onDone={onDone}>Customer {h.name}</At>{h.org_name && <span className="muted"> · {h.org_name}</span>}</>;
    case "site":
      return <><At scope={{ orgId: h.org_id, customerId: h.customer_id, siteId: h.id }} pathname="/" onDone={onDone}>Site {h.name}{h.matched_former_name && `, formerly ${h.matched_former_name}`}</At>{h.customer_name && <span className="muted"> · {h.customer_name}</span>}</>;
    case "network":
      return <><At scope={scopeOf(h, "site")} pathname="/design/wireless" facets={{ network: h.id }} onDone={onDone}>
        Network {h.name}{h.ssid && h.ssid !== h.name ? ` (${h.ssid})` : ""}</At>{place}</>;
    case "vlan_name":
      return <><At scope={scopeOf(h, "customer")} pathname="/design/vlans" onDone={onDone}>VLAN {h.vlan} {h.name}</At>{place}</>;
    case "org":
      // Found by its whole ID alone (ADR-0551 decision 2a): the way to it is
      // to make it the scope, at home, as a customer's and a site's hit are.
      return <At scope={{ orgId: h.org_id }} pathname="/" onDone={onDone}>Organization {h.name ?? h.org_id}</At>;
    default:
      // A kind this build does not know (ADR-0322): named as served, and not a link.
      return <span>{h.name ?? h.serial ?? h.kind}</span>;
  }
}

/** The object a hit names, for its menu (#2344): a device the caller holds, a
 * customer, a site, a network, an organization. A radio address, a planned
 * device and a VLAN's name have none: none is an object the menu knows. */
function entityOfHit(h: Hit): Entity | undefined {
  switch (h.kind) {
    case "claimed_device": return h.serial ? { kind: "device", serial: h.serial, name: h.name ?? undefined, switch: h.device_class === "switch", siteId: h.site_id ?? undefined, siteName: h.site_name ?? undefined, orgId: h.org_id ?? undefined, customerId: h.customer_id ?? undefined } : undefined;
    case "customer": return h.id ? { kind: "customer", id: h.id, name: h.name ?? h.id, slug: h.slug ?? undefined, orgId: h.org_id ?? undefined } : undefined;
    case "site": return h.id ? { kind: "site", id: h.id, name: h.name ?? h.id, slug: h.slug ?? undefined, orgId: h.org_id ?? undefined, customerId: h.customer_id ?? undefined } : undefined;
    case "network": return h.id && (h.ssid ?? h.name) ? { kind: "ssid", ssid: (h.ssid ?? h.name)!, networkId: h.id, siteId: h.site_id ?? undefined } : undefined;
    case "org": return { kind: "org", id: h.org_id, name: h.name ?? h.org_id, slug: h.slug ?? undefined };
    default: return undefined;
  }
}

/** How old the copy of the cell's inventory a device hit was found in is
 * (ADR-0397 decision 4, ADR-0234's age on every answer served from a copy).
 * Nothing for a device Zenith holds a record of, whose hit carries no age. */
function Listed({ at }: { at?: string }) {
  if (!at) return null;
  return <span className="small muted"> · as its cell listed it at <When value={at} /></span>;
}

/** When the state report a radio address was found in was recorded, for a hit
 * the `reported` rule found (ADR-0421): the address is one the access point
 * named itself, as of that report. Nothing for a `derived` hit, which is read
 * from the serial and carries no time. */
function Reported({ at }: { at?: string }) {
  if (!at) return null;
  // Restates ADR-0421 (S1 · B1) — the address is one the access point last reported, kept only while that report
  // was taken under the configuration and firmware it runs now, so the time is the report's and not the search's.
  return <span className="small muted"> · as it reported at <When value={at} /></span>;
}

/** A whole address that matched no device, handed to one site's Clients page
 * by its `mac` facet (ADR-0324 decision 2). With a site in scope, that site;
 * with none, the scope picker narrowed to sites (2b). */
function HandOff({ mac, onDone }: { mac: string; onDone(): void }) {
  const { scope } = useScope();
  const navigate = useNavigate();
  const { lists } = useEstate();
  const [choosing, setChoosing] = useState(false);
  const clients = (at: Scope) => ({ pathname: "/operate/wireless", search: facetSearch({ mac }, scopeSearch(at)) });
  const siteName = scope.siteId ? lists?.sites.find((s) => s.id === scope.siteId)?.name : undefined;
  return (
    // Restates ADR-0324 decision 2 — a client is looked for at one site, among the clients its access points
    // report now. The search matched no device it reads by the address, which is all this knows of it: not
    // that no device the caller can see has it, since an access point Zenith holds no record of is found only
    // from the copy of its cell's inventory, which a device claimed since the sweep's last tick is not in
    // (ADR-0397, #1805).
    <div className="shell-search-handoff">
      <p className="small">The search matched no device by the address {formatted(mac)}. If it is a client&rsquo;s, look for it at one site.</p>
      {scope.siteId
        ? <Link to={clients(scope)} onClick={onDone}>Look for {formatted(mac)} among the clients at {siteName ?? "this site"}</Link>
        : choosing
          ? (
            <span className="seg">
              <ScopeMenu lists={lists} keep={(r: Row) => r.kind === "site"}
                onPick={(r) => { setChoosing(false); onDone(); navigate(clients(r.scope)); }}
                onClose={() => setChoosing(false)} />
            </span>
          )
          : <button type="button" className="btn btn-quiet btn-sm" onClick={() => setChoosing(true)}>Choose a site to look for it at</button>}
    </div>
  );
}
